import axios from "axios";
import {
  DEFAULT_START_OF_DAY,
  MAXIMUM_SAFE_DURATION,
  PDA_PREFIX,
  SUMMARY_TYPE,
  MILESTONE_TYPE,
  PROJECT_START_MILESTONE,
  PROJECT_FINISH_MILESTONE,
  CONSTRAINT_TYPE_LATEST,
  CONSTRAINT_TYPE_FINISH_NO_LATER_THAN,
  CONSTRAINT_TYPE_EARLIEST,
  CONSTRAINT_TYPE_START_NO_EARLIER_THAN,
  CONSTRAINT_TYPE_AS_LATE_AS_POSSIBLE,
  CONSTRAINT_TYPE_AS_LATE_AS_POSSIBLE_FRONT,
  CONSTRAINT_TYPE_ASAP,
  CONSTRAINT_TYPE_ASAP_FRONT,
  CONSTRAINT_TYPE_SNET,
  CONSTRAINT_TYPE_FNLT,
  CONSTRAINT_TYPE_ALAP,
  CONSTRAINT_TYPE_MSO,
  CONSTRAINT_TYPE_MFO,
  CONSTRAINT_TYPE_MUST_START_ON,
  CONSTRAINT_TYPE_MUST_FINISH_ON,
  CONSTRAINT_TYPE_MUST_START_ON_FRONT,
  CONSTRAINT_TYPE_MUST_FINISH_ON_FRONT,
} from "../../global/constants";
import { removeTimezoneOffset } from "../DateUtils/DateUtils";
import { fromDisplayDuration, fromDisplayWork } from "../displayUnits";
import { extractFieldWithRecursion } from "../ObjectUtils/ObjectUtils";
import { reconcileTaskParents } from "../../components/Bryntum/Gantt/insertTasksHierarchy";
import { REALIGN_SCHEDULE_ACTIVITY_SEQUENCE } from "../../services/types";

export const computeAvailableWork = (workPlanned, workAssigned) => {
  const result = workPlanned - workAssigned;
  if (result < 0) return 0;
  return result;
};

export const convertSchedulerTasks = (tasks) =>
  !!tasks.length
    ? tasks.map(({ id, name, start, finish, resource, ...rest }) => ({
      id,
      Name: name,
      StartTime: start,
      EndTime: finish,
      CollabsID: resource.id,
      resource,
      ...rest,
    }))
    : [];

export const getInitialAllocation = (
  numberOfResources,
  resourceIndex,
  lastItemIndex
) => {
  const isLastItem = resourceIndex === lastItemIndex;
  const value = Math.floor(100 / numberOfResources);
  const rest = 100 % numberOfResources;
  return isLastItem ? value + rest : value;
};

// SPEC-0059 / PDR-0206: the backend's short names, its legacy names and the Bryntum values
// all resolve to the Bryntum value the Gantt works with. Never "" for a known type: a row
// mapped to "" lost its pin on the next date edit.
const constraintTypeParser = {
  [CONSTRAINT_TYPE_EARLIEST]: CONSTRAINT_TYPE_START_NO_EARLIER_THAN,
  [CONSTRAINT_TYPE_SNET]: CONSTRAINT_TYPE_START_NO_EARLIER_THAN,
  [CONSTRAINT_TYPE_LATEST]: CONSTRAINT_TYPE_FINISH_NO_LATER_THAN,
  [CONSTRAINT_TYPE_FNLT]: CONSTRAINT_TYPE_FINISH_NO_LATER_THAN,
  [CONSTRAINT_TYPE_AS_LATE_AS_POSSIBLE]: CONSTRAINT_TYPE_AS_LATE_AS_POSSIBLE_FRONT,
  [CONSTRAINT_TYPE_ALAP]: CONSTRAINT_TYPE_AS_LATE_AS_POSSIBLE_FRONT,
  [CONSTRAINT_TYPE_ASAP]: CONSTRAINT_TYPE_ASAP_FRONT,
  [CONSTRAINT_TYPE_MUST_START_ON]: CONSTRAINT_TYPE_MUST_START_ON_FRONT,
  [CONSTRAINT_TYPE_MSO]: CONSTRAINT_TYPE_MUST_START_ON_FRONT,
  [CONSTRAINT_TYPE_MUST_FINISH_ON]: CONSTRAINT_TYPE_MUST_FINISH_ON_FRONT,
  [CONSTRAINT_TYPE_MFO]: CONSTRAINT_TYPE_MUST_FINISH_ON_FRONT,
};
const BRYNTUM_CONSTRAINT_VALUES = new Set(Object.values(constraintTypeParser));

export const parseConstraintType = (constraint) => {
  const type = constraint?.type;
  if (!type) return "";
  if (type in constraintTypeParser) return constraintTypeParser[type];
  return BRYNTUM_CONSTRAINT_VALUES.has(type) ? type : "";
};

const FINISH_ANCHORED_CONSTRAINT_TYPES = new Set([
  CONSTRAINT_TYPE_LATEST,
  CONSTRAINT_TYPE_FNLT,
  CONSTRAINT_TYPE_FINISH_NO_LATER_THAN,
  CONSTRAINT_TYPE_MUST_FINISH_ON,
  CONSTRAINT_TYPE_MFO,
  CONSTRAINT_TYPE_MUST_FINISH_ON_FRONT,
]);
const START_ANCHORED_CONSTRAINT_TYPES = new Set([
  CONSTRAINT_TYPE_EARLIEST,
  CONSTRAINT_TYPE_SNET,
  CONSTRAINT_TYPE_START_NO_EARLIER_THAN,
  CONSTRAINT_TYPE_ASAP,
  CONSTRAINT_TYPE_ASAP_FRONT,
  CONSTRAINT_TYPE_MUST_START_ON,
  CONSTRAINT_TYPE_MSO,
  CONSTRAINT_TYPE_MUST_START_ON_FRONT,
]);
/** FNLT, MSO and MFO: the Gantt shows the engine's dates and never reschedules the row itself. */
const PINNED_CONSTRAINT_TYPES = new Set([
  CONSTRAINT_TYPE_FINISH_NO_LATER_THAN,
  CONSTRAINT_TYPE_MUST_START_ON_FRONT,
  CONSTRAINT_TYPE_MUST_FINISH_ON_FRONT,
]);

/** ASAP and ALAP carry no date (PDR-0231 item 4): the date cell is read-only and shows "—". */
const DATELESS_CONSTRAINT_TYPES = new Set([
  CONSTRAINT_TYPE_ASAP,
  CONSTRAINT_TYPE_ASAP_FRONT,
  CONSTRAINT_TYPE_AS_LATE_AS_POSSIBLE,
  CONSTRAINT_TYPE_ALAP,
  CONSTRAINT_TYPE_AS_LATE_AS_POSSIBLE_FRONT,
]);

/** Whether the type (backend name or Bryntum value) carries no date: ASAP, ALAP, or none at all. */
export const isDatelessConstraintType = (type) => !type || type === "none" || DATELESS_CONSTRAINT_TYPES.has(type);

/**
 * PDR-0231 item 5 / PDR-0206 item 8: a generated row with no predecessor inside its scope holds
 * SNET at the scope start until dateless ASAP ships in the engine. The cell says so when the
 * row is SNET, has no predecessor of its own scope, and its constraint was never edited by hand
 * (the backend's `userEdited` set carries CONSTRAINT once a user touched it).
 */
export const isScopeStartHold = (record) => {
  const data = record?.data || record || {};
  if (data.name === PROJECT_START_MILESTONE) return false;
  if (parseConstraintType({ type: data.constraintType }) !== CONSTRAINT_TYPE_START_NO_EARLIER_THAN) return false;
  if (Array.isArray(data.userEdited) && data.userEdited.includes("CONSTRAINT")) return false;
  const predecessors = record?.predecessors || [];
  return !predecessors.some((dependency) => {
    const from = dependency?.fromEvent;
    return from && from.name !== PROJECT_START_MILESTONE && from.data?.scopeName === data.scopeName;
  });
};

/** Whether the date of the type is a finish (FNLT, MFO), as the backend enum or the Bryntum value. */
export const isFinishAnchoredConstraintType = (type) => FINISH_ANCHORED_CONSTRAINT_TYPES.has(type);
/** Whether the Bryntum value is one the Gantt pins (manually scheduled): FNLT, MSO, MFO. */
export const isPinnedConstraintType = (bryntumValue) => PINNED_CONSTRAINT_TYPES.has(bryntumValue);

const rowIsAsLateAsPossible = (task) =>
  parseConstraintType(task.constraint) === CONSTRAINT_TYPE_AS_LATE_AS_POSSIBLE_FRONT;

/**
 * Bryntum 5.6 schedules backward every non-pinned row that follows an "as late as possible" row,
 * whatever its own constraint (ScheduledByDependenciesEarlyEventMixin: effective direction
 * "enforced"): an ASAP END behind ALAP blocks showed on its late date instead of the early date
 * the engine computed. The rule skips a manually scheduled row and stops at a manually scheduled
 * predecessor, so pinning the direct successors of the ALAP rows on the engine's dates is enough.
 * Takes the whole payload: a predecessor can sit anywhere in it.
 */
export const markRowsBehindAsLateAsPossible = (tasks) => {
  const alapIds = new Set(tasks.filter(rowIsAsLateAsPossible).map((task) => String(task.id)));
  if (!alapIds.size) return tasks;
  return tasks.map((task) => {
    if (task.summary || task.lineType === SUMMARY_TYPE || rowIsAsLateAsPossible(task)) return task;
    if (isPinnedConstraintType(parseConstraintType(task.constraint))) return task;
    const behindAlap = Object.keys(task.predecessorsTab || {}).some((id) => alapIds.has(id));
    return behindAlap ? { ...task, pinnedBehindAlap: true } : task;
  });
};

const recordIsBehindAsLateAsPossible = (record) =>
  [...(record.incomingDeps || [])].some(
    (dependency) =>
      dependency.active !== false &&
      dependency.fromEvent?.constraintType === CONSTRAINT_TYPE_AS_LATE_AS_POSSIBLE_FRONT
  );

/**
 * A row the backend keeps at its stored dates at the Date of Review (isPastReview,
 * PastReviewUtils.stampFrozen), pinned there in every Gantt. Never a summary: it spans its
 * children, and pinned on its own duration of 0 it turned into a milestone. Takes a payload row
 * or a store record.
 */
export const isFrozenAtReview = (row) => {
  const data = row?.data || row || {};
  if (data.isPastReview !== true && row?.isPastReview !== true) return false;
  return !(data.summary || data.lineType === SUMMARY_TYPE || row?.isParent || data.children?.length);
};

/**
 * Live-store twin of markRowsBehindAsLateAsPossible, for rows already loaded: once no ALAP row
 * precedes a row pinned behind one (its predecessors left ALAP while progress is recorded), it
 * goes back to the engine and follows its predecessors again instead of staying on its date.
 * Only the pins this rule set are released: FNLT / MSO / MFO keep theirs.
 */
export const refreshPinsBehindAsLateAsPossible = (records) => {
  records.forEach((record) => {
    if (!record || record.isParent || isPinnedConstraintType(record.constraintType)) return;
    const behindAlap =
      record.constraintType !== CONSTRAINT_TYPE_AS_LATE_AS_POSSIBLE_FRONT &&
      recordIsBehindAsLateAsPossible(record);
    if (behindAlap && !record.manuallyScheduled) {
      record.set({ manuallyScheduled: true, pinnedBehindAlap: true });
    } else if (!behindAlap && record.pinnedBehindAlap) {
      record.set({ manuallyScheduled: isFrozenAtReview(record), pinnedBehindAlap: false });
    }
  });
};

/**
 * After a refetch, from the payload rather than the store: the server knows whether a predecessor
 * is still ALAP, including when it left ALAP elsewhere (library, progress). A row pinned behind an
 * ALAP row that no longer has one is released and follows its predecessors again.
 */
export const syncPinsBehindAsLateAsPossible = (records, payload) => {
  const behindAlap = new Set(
    markRowsBehindAsLateAsPossible(payload)
      .filter((task) => task.pinnedBehindAlap)
      .map((task) => String(task.id))
  );
  records.forEach((record) => {
    if (record.isParent || isPinnedConstraintType(record.constraintType)) return;
    const pinned = behindAlap.has(String(record.id));
    if (pinned && !record.manuallyScheduled) {
      record.set({ manuallyScheduled: true, pinnedBehindAlap: true });
    } else if (!pinned && record.pinnedBehindAlap) {
      record.set({ manuallyScheduled: isFrozenAtReview(record), pinnedBehindAlap: false });
    }
  });
};

/**
 * After a refetch, from the payload: a row the backend keeps at its stored dates at the Date of
 * Review (isPastReview) is pinned there, one it no longer keeps goes back to the engine. Rows of a
 * payload that does not carry the flag (an edit echo) keep their pin. FNLT / MSO / MFO and the
 * rows pinned behind an ALAP row keep theirs.
 */
export const syncPinsAtDateOfReview = (records, payload) => {
  const flagById = new Map(
    (payload || [])
      .filter((task) => typeof task?.isPastReview === "boolean")
      .map((task) => [String(task.id), task.isPastReview])
  );
  records.forEach((record) => {
    if (!flagById.has(String(record.id)) || record.isParent) return;
    const frozen = flagById.get(String(record.id));
    if (frozen === !!record.isPastReview) return;
    const keptPinned = isPinnedConstraintType(record.constraintType) || record.pinnedBehindAlap;
    const changes = { isPastReview: frozen, manuallyScheduled: frozen || keptPinned };
    // Same rule as formatOneTask: a frozen milestone sits at the end of its day, so an FS
    // successor starts the next working day, as PDM schedules it.
    if (frozen && record.milestone && !isPinnedConstraintType(record.constraintType) && record.startDate) {
      const endOfDay = new Date(record.startDate);
      endOfDay.setHours(23, 59, 59, 0);
      changes.startDate = endOfDay;
      changes.endDate = endOfDay;
    }
    record.set(changes);
  });
};

/** The edited row and its direct successors: the only rows a constraint edit can pin or release. */
export const refreshPinsAroundConstraintEdit = (record) =>
  refreshPinsBehindAsLateAsPossible([
    record,
    ...[...(record?.outgoingDeps || [])].map((dependency) => dependency.toEvent),
  ]);

/**
 * SPEC-0059 §5.4 — the record fields every constraint editor sets with the type. A pin (MSO,
 * MFO, and a FNLT row in 1.x) is scheduled manually at its pin, exactly as the loader does:
 * Bryntum 5.6 would otherwise treat it as a hard constraint and raise its own "Scheduling
 * conflict" popup against the predecessor, whereas the backend flags the conflict on the
 * row. Any other type hands the row back to the engine.
 */
export const constraintPinFields = (bryntumValue, constraintDate) => {
  if (!isPinnedConstraintType(bryntumValue)) return { manuallyScheduled: false };
  if (bryntumValue === CONSTRAINT_TYPE_MUST_START_ON_FRONT) {
    return { manuallyScheduled: true, startDate: constraintDate };
  }
  return { manuallyScheduled: true };
};

/**
 * Applies a constraint edit to a Bryntum task record: `fields` and the pin state go in one
 * batched set (one engine pass), and a finish pin (MFO / FNLT) then moves the end through
 * `setEndDate(date, keepDuration)` so the start moves and the bar keeps its length (X1).
 * A plain `set({ endDate })` keeps the engine's start and shrinks the bar, and a duration
 * equal to the stored one is dropped by `set` as unchanged.
 */
export const applyConstraintPin = (record, bryntumValue, constraintDate, fields = {}) => {
  const isStartPin = bryntumValue === CONSTRAINT_TYPE_MUST_START_ON_FRONT;
  const pinDate = snapPinToWorkingDay(record?.effectiveCalendar, constraintDate, isStartPin);
  record.set({ ...fields, ...constraintPinFields(bryntumValue, pinDate) });
  if (pinDate && isPinnedConstraintType(bryntumValue) && !isStartPin) {
    record.setEndDate(pinDate, true);
  }
  refreshPinsAroundConstraintEdit(record);
};

/**
 * The date a constraint type picked for a row starts from, before the editor's own
 * normalisation: the record's own date, else its early date, else its own start or finish
 * (a finish-anchored type takes the finish). A row the engine has not computed yet
 * (API-created, imported) has no early dates, and PDM refuses a pin or a deadline without a
 * date, so the row's own dates are the last resort. As late as possible and none carry none.
 */
export const constraintDateSource = (data, bryntumValue) => {
  if (!bryntumValue || bryntumValue === "none" || bryntumValue === CONSTRAINT_TYPE_AS_LATE_AS_POSSIBLE_FRONT) {
    return null;
  }
  const source = isFinishAnchoredConstraintType(bryntumValue)
    ? data.constraintDate || data.earlyEndDate || data.endDate
    : data.constraintDate || data.earlyStartDate || data.startDate;
  return source || null;
};

/** The Program / Multi-project popup: a type picked for the row, with the date it implies. */
export const applyConstraintTypeFromPopup = (record, bryntumValue) => {
  const source = constraintDateSource(record.data, bryntumValue);
  const constraintDate = source ? normalizeConstraintDate(source, bryntumValue) : null;
  applyConstraintPin(record, bryntumValue, constraintDate, { constraintType: bryntumValue, constraintDate });
  return constraintDate;
};

/** The Program / Multi-project popup: a date picked for the row's current type. */
export const applyConstraintDateFromPopup = (record, date) => {
  const constraintDate = normalizeConstraintDate(date, record.constraintType);
  applyConstraintPin(record, record.constraintType, constraintDate, { constraintDate });
};

/**
 * `record.set(fields)` without Bryntum's automatic pin: writing a start date outside a load adds a
 * "start no earlier than" on that date (`project.addConstraintOnDateSet`, true by default), so a
 * revert that restores the start turned an ASAP row into SNET. The flag is read synchronously
 * inside the write, so it is lowered for this set only (same device as writeProjectFinishDate).
 */
export const setWithoutDatePin = (record, fields) => {
  const project = record?.project;
  const pinsOnDateSet = project?.addConstraintOnDateSet === true;
  if (pinsOnDateSet) project.setData({ addConstraintOnDateSet: false });
  try {
    record.set(fields);
  } finally {
    if (pinsOnDateSet) project.setData({ addConstraintOnDateSet: true });
  }
};

/** What the popup may change on the record — taken when it opens, set back on cancel. */
export const constraintPinSnapshot = (record) => ({
  constraintDate: record.constraintDate,
  constraintType: record.constraintType,
  manuallyScheduled: record.manuallyScheduled,
  startDate: record.startDate,
  endDate: record.endDate,
});

/**
 * SPEC-0059 §5.1 (M9): a pin typed on a non-working day of the row's calendar is placed on the
 * next (start pin) or previous (finish pin) working day, at the pin's own time of day. The
 * constraint date itself stays as typed; the name column warns about it.
 */
export const snapPinToWorkingDay = (calendar, date, forward) => {
  if (!calendar || !(date instanceof Date)) return date;
  const dayStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const dayEnd = new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1);
  if (calendar.isWorkingTime(dayStart, dayEnd)) return date;
  const working = calendar.skipNonWorkingTime(forward ? dayStart : new Date(dayEnd.getTime() - 1), forward);
  if (!(working instanceof Date)) return date;
  return new Date(
    working.getFullYear(),
    working.getMonth(),
    working.getDate(),
    date.getHours(),
    date.getMinutes(),
    date.getSeconds()
  );
};

/**
 * Parses a date value as a LOCAL date, preserving the calendar day from the
 * database regardless of the browser's timezone.
 *
 * Problem: Database stores dates as UTC midnight ("2026-03-18T00:00:00Z").
 * When parsed with `new Date()` in non-UTC timezones, the local date can shift:
 * - In negative offsets (e.g., UTC-8): March 18 00:00 UTC → March 17 16:00 local
 * - In positive offsets (e.g., UTC+1): March 18 00:00 UTC → March 18 01:00 local
 * After applying setHours(), the displayed date may be wrong.
 *
 * Solution: Extract the YYYY-MM-DD portion and create a Date at local midnight,
 * similar to ensureDate() in Grid/utils.js.
 */
const parseAsLocalDate = (value) => {
  if (value instanceof Date) return new Date(value);
  if (typeof value === "string") {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      return new Date(+match[1], +match[2] - 1, +match[3]);
    }
  }
  return new Date(value);
};

export const normalizeConstraintDate = (constraintDate, constraintType) => {
  if (!constraintDate) return constraintDate;

  // Parse as local date to preserve the calendar day from the database,
  // avoiding timezone-induced day shifts.
  const date = parseAsLocalDate(constraintDate);

  // Normalize constraint dates to standard business hours in local time.
  // Using setHours ensures activities display at correct times (e.g., 9:00 AM)
  // regardless of timezone differences.
  if (FINISH_ANCHORED_CONSTRAINT_TYPES.has(constraintType)) {
    date.setHours(23, 59, 0, 0);
  } else if (START_ANCHORED_CONSTRAINT_TYPES.has(constraintType)) {
    date.setHours(DEFAULT_START_OF_DAY, 0, 0, 0);
  }

  return date;
};

/**
 * When the user manually edits the start date of an ALAP ("as late as
 * possible") task — via the grid Start cell, the task-edit dialog, or
 * by dragging the bar — the BE scheduler will recompute the task back
 * to its old `constraintDate` on save unless we also bump that field.
 *
 * Mutates the DTO in place so the existing call sites can `dto = format(...)`
 * → `syncAlapConstraintDateWithStartDate(dto, value)` → dispatch, with no
 * branching on the constraint type at every site. The function is a no-op
 * for non-ALAP tasks, so it's safe to call unconditionally.
 *
 * The constraint TYPE stays `aslateaspossible` — only the date moves. The
 * spec ("activity stays at the chosen date") doesn't require switching
 * the type; keeping ALAP semantics preserves any downstream-cascade
 * scheduling behaviour the BE applies to ALAP tasks.
 *
 * @param {object} dto - DTO returned by formatRecordAsFixedDimensionDTO,
 *   mutated in place. Must carry `constraintType` (added by the caller
 *   before this runs — see `updateTask` in Gantt.js for the canonical
 *   "preserve constraint" pre-step).
 * @param {Date|string|number|null|undefined} newStartDate - The start
 *   date the user just chose. May already be timezone-normalised by the
 *   caller (any of removeTimezoneOffset's outputs round-trip safely).
 * @returns {object} the same DTO reference, for fluent use.
 */
export const syncAlapConstraintDateWithStartDate = (dto, newStartDate) => {
  if (!dto) return dto;
  if (dto.constraintType !== CONSTRAINT_TYPE_AS_LATE_AS_POSSIBLE_FRONT) return dto;
  if (newStartDate == null) return dto;
  dto.constraintDate = removeTimezoneOffset(newStartDate);
  return dto;
};

export const taskRecordIsSummary = (taskRecord) => {
  const lineTypeIsS = taskRecord.data?.lineType === SUMMARY_TYPE;
  const taskRecordIsParent = taskRecord.isParent;
  return lineTypeIsS || taskRecordIsParent;
};

// A task whose end date is before the Date of Review and whose progress is
// 0 (or unset) — shared by the Roadmap widget (LightweightTimeline) and the
// Bryntum Gantt's Name column renderer, so both surfaces agree on the exact
// same rule.
/**
 * The progress sent back to the backend. It used to be rounded to a whole
 * percent, which put the saved value — and the hours derived from it — up to
 * half a point away from the grey zone the user had just dragged. Two decimals,
 * like the backend's own Date-of-Review progress.
 */
export const toProgressValue = (percentDone) => Math.round(percentDone * 100) / 100;

export const isPastZeroProgress = (task, dateOfReview) => {
  if (!task || !dateOfReview || !task.endDate) return false;
  const progress = task.percentDone ?? 0;
  return new Date(task.endDate) < new Date(dateOfReview) && progress === 0;
};

export const isDimensionalResourceAssigned = (event) => {
  const valueToCheck = event.editorContext.column.originalData.id;
  const keys = Object.keys(
    event.editorContext.record.originalData.ganttResourceAllocation
  );
  return keys.includes(valueToCheck.toString());
};

// Read from the loaded row, not from Bryntum's `milestone`/`isMilestone`: the
// engine flags any 0-day task as a milestone, including the 0 d it derives
// itself, so it cannot tell a real milestone from a collapsed span.
const isLoadedAsMilestone = (taskRecord) =>
  taskRecord.originalData?.milestone === true ||
  taskRecord.data?.lineType === MILESTONE_TYPE;

/**
 * In Fixed Effort the engine derives duration = effort / (units x hours per
 * day). With no effort it derives 0 d and finish = start: that is the
 * engine's arithmetic, not a request to turn the span into a milestone (the
 * backend converts a 0-day duration into one and wipes the work, leaving the
 * row stuck at 0 d). Such a duration must never be sent.
 */
export const hasNoFixedEffortBudget = (taskRecord) =>
  !(taskRecord.effort > 0) && !isLoadedAsMilestone(taskRecord);

export const isZeroWorkFixedEffortCollapse = (dto, taskRecord) =>
  dto.fixedDimension === "FixedEffort" &&
  dto.duration === 0 &&
  !(dto.effort > 0) &&
  hasNoFixedEffortBudget(taskRecord);

/**
 * The early dates a save sends for a row. A row the Gantt holds on its own dates (an ALAP row,
 * one pinned behind it, a deadline pin, one kept at the Date of Review) is on its early dates
 * there, as PDM stores it (an ALAP activity's early dates are its late dates). The engine's
 * forward pass ignores the pin and gives it the project's start instead: saved as its early
 * start, the row was drawn on the project start at the next load (the multi-project Gantt
 * draws early dates).
 */
export const savedEarlyDates = (taskRecord) => {
  const heldOnItsDates =
    taskRecord.manuallyScheduled &&
    (taskRecord.constraintType === CONSTRAINT_TYPE_AS_LATE_AS_POSSIBLE_FRONT ||
      taskRecord.pinnedBehindAlap ||
      isPinnedConstraintType(taskRecord.constraintType) ||
      isFrozenAtReview(taskRecord));
  return heldOnItsDates
    ? { earlyStartDate: taskRecord.startDate, earlyEndDate: taskRecord.endDate }
    : { earlyStartDate: taskRecord.earlyStartDate, earlyEndDate: taskRecord.earlyEndDate };
};

export function formatRecordAsFixedDimensionDTO(taskRecord) {
  const resourceAllocationEntries = taskRecord.assignments
    .map((assignment) => [assignment.resourceId, assignment.units])
    .filter(([key]) => !!key);
  const resourceAllocation = Object.fromEntries(resourceAllocationEntries);
  // Backend contract: duration in days, effort in hours. With display
  // units active, editors can leave a week/month (or day-of-work) united
  // magnitude on the record — convert here as the last line of defense
  // for every DTO path (cell edit, drag, task-edit dialog).
  const durationDays = fromDisplayDuration(
    taskRecord.duration,
    taskRecord.durationUnit || "day"
  );
  const effortHours = fromDisplayWork(
    taskRecord.effort,
    taskRecord.effortUnit || "hour"
  );
  const duration =
    durationDays < MAXIMUM_SAFE_DURATION
      ? durationDays
      : taskRecord.originalData.duration;
  const early = savedEarlyDates(taskRecord);
  return {
    sourceId: taskRecord.id,
    fixedDimension: taskRecord.schedulingMode,
    resourceAllocation,
    duration,
    effort: effortHours,
    startDate: removeTimezoneOffset(taskRecord.startDate),
    endDate: removeTimezoneOffset(taskRecord.endDate),
    earlyStartDate: removeTimezoneOffset(early.earlyStartDate),
    earlyEndDate: removeTimezoneOffset(early.earlyEndDate),
    lateStartDate: removeTimezoneOffset(taskRecord.lateStartDate),
    lateEndDate: removeTimezoneOffset(taskRecord.lateEndDate),
    deadlineDate:
      taskRecord.deadlineDate == null
        ? null
        : removeTimezoneOffset(taskRecord.deadlineDate),
  };
}

export function extractIdsOfTasksTree(tasksTree) {
  return extractFieldWithRecursion(tasksTree, "id", "children");
}

/**
 * Brings both stores back in line with the server's sequence after a gesture.
 *
 * The response carries `parentSaId` — a row id — alongside the ordinals, so the
 * rows are re-parented on an identity rather than on a number the backend
 * renumbers across the whole scenario on every add, move-to and reorder. That
 * also removes the reason this used to wait 200 ms before touching the store:
 * the wait existed so the store would still hold the PREVIOUS numbering when the
 * ordinals were resolved against it, which is not a thing that can be relied on
 * once someone else is editing the same scenario.
 *
 * `dispatch` is not optional. Refreshing the Bryntum records alone left the Redux
 * rows on the numbering they were read with, so the client held two generations
 * of ordinals at once — and every later incremental pass re-parented the whole
 * Gantt from the Redux one. A row whose ordinal had since been handed to another
 * row moved under it, subtree included; on a scenario holding two copies of the
 * same scope the drift is the size of what was inserted, so it landed on the twin.
 */
export function realignSequenceOnGantt(
  taskStore,
  scenarioId,
  dispatch,
  atUxId = 3
) {
  return axios
    .get(`${PDA_PREFIX}/schedule-activity/aligned-sequence/${scenarioId}/${atUxId}`)
    .then(({ data }) => {
      data.forEach(({ id, uxId, parentUxId }) => {
        realignSequenceForRecord(taskStore.getById(id), uxId, parentUxId);
      });
      // Writing the scalars leaves the row where it was: the ordinals describe the
      // hierarchy, they do not apply it. Without this the store disagreed with the
      // server until a full refetch.
      reconcileTaskParents(taskStore, data);
      dispatch({ type: REALIGN_SCHEDULE_ACTIVITY_SEQUENCE, payload: data });
    });
}

function realignSequenceForRecord(record, newUxId, newParentUxId) {
  if (!record) return;

  const uxIdChanged = record.uxId !== newUxId,
    parentUxIdChanged = record.parentUxId !== newParentUxId;
  if (uxIdChanged) {
    record.uxId = newUxId;
    record.data.uxId = newUxId;
  }
  if (parentUxIdChanged) {
    record.parentUxId = newParentUxId;
    record.data.parentUxId = newParentUxId;
  }
  if (uxIdChanged || parentUxIdChanged) record.commitAsync();
}
